import { CUSTOMER_ALIAS_KEY, resolveCustomer } from "./customerAlias";   // #CUST_ALIAS_V1
import CustomerAliasTool from "./CustomerAliasTool";   // #CUST_ALIAS_TOOL_V1
import { useState, useEffect, useMemo } from "react";

// #PNL_BY_CATEGORY_V1 (2026-09-09) — Bill/Aaron: "piped in a few places... P&L button but displays
// bulk list but not year or individual job. Estimating dash but unable to make it clean." There were
// three separate Job P&L-by-customer implementations (Owner Dash's own bulk modal, Job Cost's rollup,
// Estimating's rollup) — same math, three copies, drifting features (Owner Dash's had no year filter
// and un-clickable job rows). This is the one shared component: roll up by Customer/City/Pipe Size/
// Utility Service, pick a year (or see the #COST_PNL_YEARTREND_V1 year-by-year trend across every
// year on file), see Revenue/Cost/GP/LF/Planned tiles, a breakeven table, and every job underneath it
// — click a job to drill into it via whatever `onDrillJob` does in that module. The Customer Alias
// tool (duplicate-name merge) sits collapsed at the top by default in every host now, theme-matched
// instead of always-white.
//
// Props:
//   rows        — cost-history-shaped job rows: { duc, customer, city, pipe, rev, cost, lf, bores, yr }
//   jobs        — Job List records (for Planned Revenue: j.displayDuc||j.duc, j.prev)
//   custAlias   — raw customer name -> canonical spelling map (same store everywhere)
//   onAliasChange — called with the updated alias map when a merge/unmerge happens
//   sg, ss      — Supabase get/set (passed straight through to CustomerAliasTool)
//   canEdit     — gates the alias merge tool (view-only roles don't get it)
//   theme       — { card, tileBg, border, text, muted, accent, accentText, blue, green, red, inputBg,
//                   groupHeaderBg } — see DEFAULT_THEME below for the light (Job Cost) values
//   onDrillJob  — (duc) => void, called when a JOBS IN THIS ROLLUP row is clicked
//   initialDim  — 'customer' | 'city' | 'pipe' | 'svc', default 'customer'
export const DEFAULT_THEME = {
  card:'#FFFFFF', tileBg:'#FFFFFF', border:'#E2E8F0', text:'#111827', muted:'#6B7280',
  accent:'#E85D24', accentText:'#FFFFFF', blue:'#1D4ED8', green:'#15803D', red:'#DC2626',
  inputBg:'#FFFFFF', groupHeaderBg:'#FFFBEB',
};

const _nd  = s => String(s||'').trim().toUpperCase().replace(/^CP-/,'').replace(/-CP$/,'');
const _yrOf = d => { const m = _nd(d).match(/^[EFGPSW](\d\d)/); return m ? 1980 + Number(m[1]) : null; };
const _jobYr = r => Number(r.yr) || _yrOf(r.duc);
const PNL_SVC = { E:'Electric', W:'Water', G:'Gas', F:'Fiber', S:'Sewer', P:'Pipeline' };
const pnlDimLabel = { customer:'Customer', city:'City', pipe:'Pipe Size', svc:'Utility Service' };

// #PNL_LIVE_CY_V1 (2026-10-22) — Bill: "AR Revenue shows $1.8M, P&L search shows $876K" (Narcomey 2026). The P&L ran entirely
// off `cost-history`, a snapshot last refreshed in early July. Current-year jobs now use LIVE numbers: revenue summed from the
// Revenue invoice lines (revenue-input, this year's pulls) and cost from the weekly cost upload (cost-job-<year>); any job
// billed or costed this year that the snapshot didn't have gets added. 'Year' = billing year, same as Revenue/AR. Prior years still come from cost-history unchanged. Same fix lands in Owner Dash, Job Cost and Estimating.
const _pull = s => { const m = String(s||'').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/); return m ? (m[3].length===2 ? 2000+Number(m[3]) : Number(m[3])) : null; };
function liveRows(rows, revLines, costJob, jobs, cy) {
  const byJob = new Map();
  (revLines || []).forEach(l => {
    const d = _nd(l.duc); if (!d) return;   // every job BILLED this year (cost-history's yr = billing year, e.g. E4511301 is a 2026 row)
    const o = byJob.get(d) || { rev:0, lf:0, bores:0, customer:'', pm:'' };
    o.rev += Number(l.invAmnt)||0; o.lf += Number(l.lf)||0; o.bores += Number(l.bore)||0;
    if (!o.customer && l.customer) o.customer = l.customer; if (!o.pm && l.pm) o.pm = l.pm;
    byJob.set(d, o);
  });
  const cj = {}; Object.entries(costJob || {}).forEach(([k,v]) => { const d = _nd(k); if (d) cj[d] = (cj[d]||0) + (Number(v)||0); });
  const jobByNd = new Map(); (jobs || []).forEach(j => { const k = _nd(j.displayDuc||j.duc); if (k && !jobByNd.has(k)) jobByNd.set(k, j); });
  const seen = new Set(); const out = [];
  (rows || []).forEach(r => {
    const d = _nd(r.duc);
    if (_jobYr(r) !== cy) { out.push(r); return; }
    if (seen.has(d)) return; seen.add(d);
    const lv = byJob.get(d);
    out.push({ ...r, rev: lv ? lv.rev : 0, lf: lv ? lv.lf : 0, bores: lv ? lv.bores : r.bores,
      cost: cj[d] != null ? cj[d] : (Number(r.cost)||0), customer: r.customer || (lv && lv.customer) || '' });
  });
  new Set([...byJob.keys(), ...Object.keys(cj)]).forEach(d => {
    if (seen.has(d)) return; seen.add(d);
    const lv = byJob.get(d) || { rev:0, lf:0, bores:0, customer:'', pm:'' }; const j = jobByNd.get(d) || {};
    out.push({ duc:d, customer: lv.customer || j.cust || j.customer || '', city: j.city || '', pm: lv.pm || j.pm || '',
      rev: lv.rev, lf: lv.lf, bores: lv.bores, cost: cj[d] || 0, yr: cy, live:true });
  });
  return out;
}

export default function JobPnlByCategory({ rows, jobs, custAlias, onAliasChange, sg, ss, canEdit, theme, onDrillJob, initialDim }) {
  const T = { ...DEFAULT_THEME, ...(theme||{}) };
  const cy = new Date().getFullYear();
  const [live, setLive] = useState(null);   // { rev:[...], cost:{...} } — #PNL_LIVE_CY_V1
  useEffect(() => { if (!sg) return; let off = false; (async () => {
    let rev = null, cost = null; try { rev = await sg('revenue-input'); } catch {} try { cost = await sg(`cost-job-${cy}`); } catch {}
    if (!off) setLive({ rev: Array.isArray(rev) ? rev.filter(l => _pull(l.pullDate) === cy || !l.pullDate) : [], cost: (cost && typeof cost === 'object' && !Array.isArray(cost)) ? cost : {} });
  })(); return () => { off = true; }; }, [sg, cy]);
  const _rows = useMemo(() => live ? liveRows(rows || [], live.rev, live.cost, jobs || [], cy) : (rows || []), [rows, live, jobs, cy]);
  const _jobs = jobs || [];
  const _alias = custAlias || {};

  const [pnlDim, setPnlDim] = useState(initialDim || 'customer');
  // #PNL_YR_DEFAULT_V1 (2026-10-30) — Bill: "pull by customer and default to current year, then allow to expand years prior".
  // Opens on Customer + this year. yrBack widens the window one prior year at a time (2026 → 2025–2026 → 2024–2026 …).
  const [pnlYr, setPnlYr]   = useState(cy);
  const [yrBack, setYrBack] = useState(0);
  const [pnlVal, setPnlVal] = useState('');

  const $    = n => '$' + Math.round(Number(n)||0).toLocaleString();
  const pct  = (g,r) => r ? `${(g/r*100).toFixed(1)}%` : '—';
  const perLF = (v,lf) => lf ? '$' + (Number(v)/lf).toFixed(2) : '—';
  const tileSm = (label,val,sub,clr) => (
    <div style={{ background:T.tileBg, border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 10px', flex:'1 1 105px', minWidth:95 }}>
      <div style={{ fontSize:9.5, color:T.muted, fontWeight:700, letterSpacing:0.4 }}>{label}</div>
      <div style={{ fontSize:15, fontWeight:900, color:clr||T.text, marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{val}</div>
      {sub && <div style={{ fontSize:9.5, color:T.muted, marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{sub}</div>}
    </div>
  );

  const custRawCounts = (() => {
    const m = {};
    _rows.forEach(r => { const c = String(r.customer||'').trim(); if (c) m[c] = (m[c]||0)+1; });
    return Object.entries(m).map(([name,count]) => ({ name, count }));
  })();

  const years = [...new Set([cy, ..._rows.map(_jobYr).filter(Boolean)])].filter(y => y>=2015 && y<=new Date().getFullYear()+1).sort((a,b) => b - a);
  const pnlDimVal = (r, dim) => dim==='customer' ? resolveCustomer(_alias, r.customer) : dim==='city' ? (r.city||'') : dim==='pipe' ? (String(r.pipe||'')) : (PNL_SVC[(_nd(r.duc)[0]||'').toUpperCase()] || '');
  const pnlYrRows = pnlYr === 'ALL' ? _rows : _rows.filter(r => { const y = _jobYr(r); return y <= pnlYr && y >= pnlYr - yrBack; });
  const yrLabel = pnlYr === 'ALL' ? 'all years' : (yrBack ? `${pnlYr - yrBack}–${pnlYr}` : String(pnlYr));
  const nextBackYr = pnlYr === 'ALL' ? null : pnlYr - yrBack - 1;
  const canGoBack = nextBackYr != null && years.some(y => y <= nextBackYr);
  const pnlOptions = [...new Set([...pnlYrRows.map(r => pnlDimVal(r, pnlDim)).filter(Boolean), ...(pnlVal ? [pnlVal] : [])])].sort((a,b) => String(a).localeCompare(String(b)));
  const pnlMatches = pnlVal ? pnlYrRows.filter(r => pnlDimVal(r, pnlDim) === pnlVal) : [];
  const _plannedByNd = (() => { const m=new Map(); _jobs.forEach(j=>{ const k=_nd(j.displayDuc||j.duc); if(k) m.set(k, (m.get(k)||0)+(Number(j.prev)||0)); }); return m; })();
  const pnlAgg = pnlMatches.reduce((s2,r) => ({ rev: s2.rev+(Number(r.rev)||0), cost: s2.cost+(Number(r.cost)||0), lf: s2.lf+(Number(r.lf)||0), planned: s2.planned+(_plannedByNd.get(_nd(r.duc))||0) }), { rev:0, cost:0, lf:0, planned:0 });

  // #COST_PNL_YEARTREND_V1 — year-by-year trend for this rollup, only meaningful across "All years".
  const pnlYearTrend = (() => {
    if (!pnlVal || (pnlYr !== 'ALL' && !yrBack)) return [];
    const byYr = new Map();
    pnlMatches.forEach(r => {
      const y = _jobYr(r); if (!y) return;
      const cur = byYr.get(y) || { yr:y, rev:0, cost:0, lf:0, bores:0, n:0 };
      cur.rev += Number(r.rev)||0; cur.cost += Number(r.cost)||0; cur.lf += Number(r.lf)||0; cur.bores += Number(r.bores)||0; cur.n += 1;
      byYr.set(y, cur);
    });
    return [...byYr.values()].sort((a,b) => a.yr - b.yr);
  })();

  return (
    <div>
      <CustomerAliasTool sg={sg} ss={ss} names={custRawCounts} canEdit={canEdit} onChange={onAliasChange} theme={T} />
      <div style={{ display:'flex', gap:14, flexWrap:'wrap', alignItems:'flex-end', marginBottom:12 }}>
        <div>
          <div style={{ fontSize:10, color:T.muted, fontWeight:700, marginBottom:3 }}>ROLL UP BY</div>
          <div style={{ display:'flex', gap:5 }}>
            {['customer','city','pipe','svc'].map(d => (
              <button key={d} onClick={()=>{ setPnlDim(d); setPnlVal(''); }}
                style={{ fontSize:11, fontWeight:700, padding:'5px 10px', borderRadius:6, cursor:'pointer',
                  border:`1px solid ${pnlDim===d?T.accent:T.border}`, background:pnlDim===d?T.accent:T.inputBg, color:pnlDim===d?T.accentText:T.text }}>{pnlDimLabel[d]}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize:10, color:T.muted, fontWeight:700, marginBottom:3 }}>YEAR</div>
          <select value={pnlYr} onChange={e=>{ setPnlYr(e.target.value==='ALL'?'ALL':Number(e.target.value)); setYrBack(0); /* #PNL_YEAR_KEEP_V1 — Bill: changing the year cleared the customer; it now stays picked */ }}
            style={{ fontSize:12.5, padding:'5px 8px', border:`1px solid ${T.border}`, borderRadius:6, background:T.inputBg, color:T.text }}>
            <option value="ALL">All years</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        {pnlYr !== 'ALL' && (canGoBack || yrBack > 0) && (
          <div>{/* #PNL_YR_DEFAULT_V1 — expand to prior years */}
            <div style={{ fontSize:10, color:T.muted, fontWeight:700, marginBottom:3 }}>PRIOR YEARS</div>
            <div style={{ display:'flex', gap:5 }}>
              {canGoBack && <button onClick={()=>setYrBack(b=>b+1)} title={`Add ${nextBackYr} to the view`}
                style={{ fontSize:11, fontWeight:700, padding:'5px 10px', borderRadius:6, cursor:'pointer', border:`1px solid ${T.border}`, background:T.inputBg, color:T.text, whiteSpace:'nowrap' }}>+ {nextBackYr}</button>}
              {yrBack > 0 && <button onClick={()=>setYrBack(0)} title={`Back to ${pnlYr} only`}
                style={{ fontSize:11, fontWeight:700, padding:'5px 10px', borderRadius:6, cursor:'pointer', border:`1px solid ${T.accent}`, background:T.accent, color:T.accentText, whiteSpace:'nowrap' }}>{yrLabel} ✕</button>}
            </div>
          </div>
        )}
        <div style={{ flex:'1 1 220px', minWidth:180 }}>
          <div style={{ fontSize:10, color:T.muted, fontWeight:700, marginBottom:3 }}>{pnlDimLabel[pnlDim].toUpperCase()}</div>
          <select value={pnlVal} onChange={e=>setPnlVal(e.target.value)}
            style={{ width:'100%', fontSize:12.5, padding:'5px 8px', border:`1px solid ${T.border}`, borderRadius:6, boxSizing:'border-box', background:T.inputBg, color:T.text }}>
            <option value="">Pick a {pnlDimLabel[pnlDim].toLowerCase()}…</option>
            {pnlOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      {pnlVal && pnlMatches.length === 0 && <div style={{ fontSize:12, color:T.muted }}>No jobs for {pnlVal} in {pnlYr==='ALL'?'any year':yrLabel}.</div>}

      {pnlVal && pnlMatches.length > 0 && (() => {
        const { rev, cost, lf, planned } = pnlAgg;
        const gp = rev - cost;
        const actualRate = lf ? rev / lf : null;
        const planVar = planned ? rev - planned : null;
        const MG = [0, 0.15, 0.20, 0.30, 0.40];
        return (
          <div>
            <div style={{ fontSize:14, fontWeight:900, marginBottom:8, color:T.text }}>
              {pnlVal} <span style={{ fontSize:12, fontWeight:600, color:T.muted }}>· {pnlDimLabel[pnlDim]} · {yrLabel} · {pnlMatches.length} job{pnlMatches.length===1?'':'s'}</span>
            </div>

            {pnlYearTrend.length > 0 && (() => {
              const totBores = pnlYearTrend.reduce((s,y)=>s+y.bores, 0);
              const totGp = pnlAgg.rev - pnlAgg.cost;
              return (
                <div style={{ marginBottom:14, overflowX:'auto' }}>
                  <div style={{ fontSize:10.5, color:T.muted, fontWeight:700, marginBottom:4 }}>YEAR-BY-YEAR</div>
                  <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%' }}>
                    <thead><tr>
                      <th style={{ padding:'3px 8px 3px 0', textAlign:'left', color:T.muted, fontSize:10, fontWeight:800 }}>YEAR</th>
                      <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>REV</th>
                      <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>COST</th>
                      <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>GP</th>
                      <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>GP%</th>
                      <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>LF</th>
                      <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>BORES</th>
                      <th style={{ padding:'3px 0 3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>JOBS</th>
                    </tr></thead>
                    <tbody>
                      {pnlYearTrend.map(y => {
                        const ygp = y.rev - y.cost;
                        return (
                          <tr key={y.yr} onClick={()=>{ setPnlYr(y.yr); setYrBack(0); }} title="View this year only"
                            style={{ borderTop:`1px solid ${T.border}`, cursor:'pointer' }}>
                            <td style={{ padding:'3px 8px 3px 0', fontWeight:700, color:T.blue }}>{y.yr}</td>
                            <td style={{ padding:'3px 8px', textAlign:'right', color:T.text }}>{$(y.rev)}</td>
                            <td style={{ padding:'3px 8px', textAlign:'right', color:T.text }}>{$(y.cost)}</td>
                            <td style={{ padding:'3px 8px', textAlign:'right', fontWeight:700, color:ygp>=0?T.green:T.red }}>{$(ygp)}</td>
                            <td style={{ padding:'3px 8px', textAlign:'right', color:T.text }}>{pct(ygp, y.rev)}</td>
                            <td style={{ padding:'3px 8px', textAlign:'right', color:T.text }}>{y.lf.toLocaleString()}</td>
                            <td style={{ padding:'3px 8px', textAlign:'right', color:T.text }}>{y.bores.toLocaleString()}</td>
                            <td style={{ padding:'3px 0 3px 8px', textAlign:'right', color:T.text }}>{y.n}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ borderTop:`2px solid ${T.border}`, fontWeight:800 }}>
                        <td style={{ padding:'4px 8px 4px 0', color:T.text }}>TOTAL</td>
                        <td style={{ padding:'4px 8px', textAlign:'right', color:T.text }}>{$(pnlAgg.rev)}</td>
                        <td style={{ padding:'4px 8px', textAlign:'right', color:T.text }}>{$(pnlAgg.cost)}</td>
                        <td style={{ padding:'4px 8px', textAlign:'right', color:totGp>=0?T.green:T.red }}>{$(totGp)}</td>
                        <td style={{ padding:'4px 8px', textAlign:'right', color:T.text }}>{pct(totGp, pnlAgg.rev)}</td>
                        <td style={{ padding:'4px 8px', textAlign:'right', color:T.text }}>{pnlAgg.lf.toLocaleString()}</td>
                        <td style={{ padding:'4px 8px', textAlign:'right', color:T.text }}>{totBores.toLocaleString()}</td>
                        <td style={{ padding:'4px 0 4px 8px', textAlign:'right', color:T.text }}>{pnlMatches.length}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ fontSize:10.5, color:T.muted, marginTop:4 }}>Click a year to drill into that year alone.</div>
                </div>
              );
            })()}

            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
              {tileSm('Revenue', $(rev), perLF(rev,lf)+'/LF blended', T.blue)}
              {tileSm('Cost', $(cost), perLF(cost,lf)+'/LF', T.accent)}
              {tileSm('Gross Profit', $(gp), pct(gp,rev)+' margin', gp>=0?T.green:T.red)}
              {tileSm('LF Invoiced', lf.toLocaleString(), 'ft · all jobs', T.text)}
              {planned > 0 && tileSm('Planned Revenue', $(planned), 'value on the Job List', T.text)}
              {planned > 0 && tileSm(planVar>=0?'Over Plan':'Under Plan', $(Math.abs(planVar)), pct(Math.abs(planVar),planned)+' vs plan', planVar>=0?T.green:T.red)}
            </div>

            {cost > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:10.5, color:T.muted, fontWeight:700, marginBottom:2 }}>TO BE PROFITABLE — BLENDED ACROSS THESE JOBS</div>
                {actualRate !== null && (
                  <div style={{ fontSize:11.5, marginBottom:6, color:T.text }}>
                    Billed at <b>${actualRate.toFixed(2)}/LF</b> blended · {rev >= cost ? <span style={{ color:T.green, fontWeight:700 }}>covered cost</span> : <span style={{ color:T.red, fontWeight:700 }}>below breakeven</span>}
                  </div>
                )}
                <table style={{ borderCollapse:'collapse', fontSize:11.5 }}>
                  <thead><tr>
                    <th style={{ padding:'3px 10px 3px 0', textAlign:'left', color:T.muted, fontSize:10, fontWeight:800 }}>MARGIN</th>
                    <th style={{ padding:'3px 10px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>REVENUE NEEDED</th>
                    {lf > 0 && <th style={{ padding:'3px 10px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>RATE $/LF</th>}
                    {lf > 0 && actualRate !== null && <th style={{ padding:'3px 0 3px 10px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>VS BILLED</th>}
                  </tr></thead>
                  <tbody>
                    {MG.map(m => {
                      const need = cost / (1 - m);
                      const rate = lf ? need / lf : null;
                      const hitIt = rev >= need;
                      return (
                        <tr key={m} style={{ borderTop:`1px solid ${T.border}` }}>
                          <td style={{ padding:'3px 10px 3px 0', fontWeight:700, color:T.text }}>{m === 0 ? 'Breakeven' : (m * 100).toFixed(0) + '% GP'}</td>
                          <td style={{ padding:'3px 10px', textAlign:'right', color:T.text }}>{$(need)}</td>
                          {lf > 0 && <td style={{ padding:'3px 10px', textAlign:'right', fontWeight:700, color:T.text }}>${rate.toFixed(2)}</td>}
                          {lf > 0 && actualRate !== null && (
                            <td style={{ padding:'3px 0 3px 10px', textAlign:'right', fontWeight:700, color:hitIt ? T.green : T.red }}>
                              {hitIt ? '✓ hit' : '+$' + (rate - actualRate).toFixed(2) + '/LF'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ fontSize:10.5, color:T.muted, fontWeight:700, marginBottom:4 }}>JOBS IN THIS ROLLUP</div>
            <div style={{ overflowX:'auto' }}>
            <table style={{ borderCollapse:'collapse', fontSize:11.5, width:'100%' }}>
              <thead><tr>
                <th style={{ padding:'3px 8px 3px 0', textAlign:'left', color:T.muted, fontSize:10, fontWeight:800 }}>DUC</th>
                <th style={{ padding:'3px 8px', textAlign:'left', color:T.muted, fontSize:10, fontWeight:800 }}>CUSTOMER</th>
                <th style={{ padding:'3px 8px', textAlign:'left', color:T.muted, fontSize:10, fontWeight:800 }}>CITY</th>
                <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>PLANNED</th>
                <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>REV</th>
                <th style={{ padding:'3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>COST</th>
                <th style={{ padding:'3px 0 3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>MARGIN</th>
                <th style={{ padding:'3px 0 3px 8px', textAlign:'right', color:T.muted, fontSize:10, fontWeight:800 }}>VS PLAN</th>
              </tr></thead>
              <tbody>
                {pnlMatches.slice().sort((a,b)=>(Number(b.rev)||0)-(Number(a.rev)||0)).map(r => {
                  const rgp = (Number(r.rev)||0) - (Number(r.cost)||0);
                  const rPlanned = _plannedByNd.get(_nd(r.duc)) || 0;
                  const rVar = rPlanned ? (Number(r.rev)||0) - rPlanned : null;
                  return (
                    <tr key={r.duc} style={{ borderTop:`1px solid ${T.border}`, cursor:'pointer' }} onClick={()=>onDrillJob && onDrillJob(r.duc)} title="View this job's own P&L">
                      <td style={{ padding:'3px 8px 3px 0', fontWeight:700, color:T.blue }}>{r.duc}</td>
                      <td style={{ padding:'3px 8px', color:T.text }}>{resolveCustomer(_alias, r.customer)||'—'}</td>
                      <td style={{ padding:'3px 8px', color:T.text }}>{r.city||'—'}</td>
                      <td style={{ padding:'3px 8px', textAlign:'right', color:T.muted }}>{rPlanned ? $(rPlanned) : '—'}</td>
                      <td style={{ padding:'3px 8px', textAlign:'right', color:T.text }}>{$(r.rev)}</td>
                      <td style={{ padding:'3px 8px', textAlign:'right', color:T.text }}>{$(r.cost)}</td>
                      <td style={{ padding:'3px 0 3px 8px', textAlign:'right', fontWeight:700, color:rgp>=0?T.green:T.red }}>{pct(rgp, r.rev)}</td>
                      <td style={{ padding:'3px 0 3px 8px', textAlign:'right', fontWeight:700, color: rVar==null ? T.muted : (rVar>=0?T.green:T.red) }}>{rVar==null ? '—' : (rVar>=0?'▲ ':'▼ ')+$(Math.abs(rVar))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
